/**
 * Floating GoSnel AI — sits above the WhatsApp FAB.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Fab,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SendIcon from '@mui/icons-material/Send';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import LpAiMessageBody from './LpAiMessageBody';

const SUGGESTIONS = [
  'How do walk-in QR codes work?',
  'How do I promote a product?',
  'How do wallet top-ups work?',
  'How do I get order alerts?',
];

const STORAGE_KEY = 'lp_gosnel_ai_conversation_id';

export default function LpGosnelAiFab() {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [conversationId, setConversationId] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || '';
    } catch {
      return '';
    }
  });
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const persistConversationId = (id) => {
    setConversationId(id || '');
    try {
      if (id) localStorage.setItem(STORAGE_KEY, id);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (open) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => inputRef.current?.focus?.(), 120);
    }
  }, [open, messages, sending]);

  const reset = () => {
    setMessages([]);
    setError('');
    setInput('');
    persistConversationId('');
  };

  const send = useCallback(
    async (text) => {
      const message = (text ?? input).trim();
      if (!message || sending) return;
      setError('');
      setInput('');
      const nextHistory = [...messages, { role: 'user', content: message }];
      setMessages(nextHistory);
      setSending(true);
      try {
        const res = await api.post('/api/vendor/lp-ai/chat', {
          message,
          history: nextHistory.map((m) => ({ role: m.role, content: m.content })),
          conversation_id: conversationId || undefined,
        });
        const reply =
          res.data?.reply ||
          res.data?.message ||
          res.data?.content ||
          'Sorry — I could not generate a reply.';
        const cid = res.data?.conversation_id || res.data?.id;
        if (cid) persistConversationId(cid);
        setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
      } catch (err) {
        if (err?.response?.status === 404) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content:
                'GoSnel AI for Local Partners is not live on the API yet. Use WhatsApp Partner Support below in the meantime. Try /orders, /products, /ads, or /topup while you wait.',
            },
          ]);
        } else {
          setError('GoSnel AI is unavailable right now.');
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content:
                'Sorry — I could not answer that just now. Try again, or use Support (WhatsApp below).',
            },
          ]);
        }
      } finally {
        setSending(false);
      }
    },
    [conversationId, input, messages, sending]
  );

  return (
    <>
      <Tooltip title="GoSnel AI — ask anything" placement="left">
        <Fab
          color="primary"
          size="medium"
          aria-label="GoSnel AI"
          onClick={() => setOpen(true)}
          sx={{
            position: 'fixed',
            right: { xs: 16, sm: 24 },
            bottom: { xs: 144, sm: 152 },
            zIndex: 1050,
            bgcolor: '#0f766e',
            color: '#fff',
            '&:hover': { bgcolor: '#0d9488' },
          }}
        >
          <AutoAwesomeIcon />
        </Fab>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: fullScreen ? 0 : 3,
            height: fullScreen ? '100%' : 'min(720px, 86vh)',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle sx={{ py: 1.5, pr: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AutoAwesomeIcon color="primary" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography fontWeight={800} lineHeight={1.2}>
                GoSnel AI
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Links open portal pages — reopen this chat anytime to continue.
              </Typography>
            </Box>
            <IconButton size="small" onClick={reset} title="New chat">
              <RestartAltIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              title="Open full page"
              onClick={() => {
                setOpen(false);
                navigate('/ai');
              }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            bgcolor: '#f8fafc',
            px: 1.5,
            py: 1.5,
          }}
        >
          <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {messages.length === 0 && (
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
                {SUGGESTIONS.map((s) => (
                  <Chip
                    key={s}
                    label={s}
                    clickable
                    size="small"
                    onClick={() => send(s)}
                    disabled={sending}
                    sx={{ fontWeight: 700 }}
                  />
                ))}
              </Stack>
            )}
            {messages.map((m, i) => {
              const isUser = m.role === 'user';
              return (
                <Paper
                  key={`${i}-${m.role}`}
                  elevation={0}
                  sx={{
                    alignSelf: isUser ? 'flex-end' : 'flex-start',
                    maxWidth: '92%',
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: isUser ? '#0f766e' : '#fff',
                    color: isUser ? '#fff' : 'text.primary',
                    border: isUser ? 'none' : '1px solid #e2e8f0',
                    wordBreak: 'break-word',
                  }}
                >
                  <LpAiMessageBody
                    content={m.content}
                    invert={isUser}
                    conversationId={conversationId}
                    onInternalNavigate={() => setOpen(false)}
                  />
                </Paper>
              );
            })}
            {sending && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 0.5 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">
                  Thinking…
                </Typography>
              </Stack>
            )}
            <div ref={bottomRef} />
          </Box>

          {error ? (
            <Typography variant="caption" color="error" sx={{ px: 0.5 }}>
              {error}
            </Typography>
          ) : null}

          <Stack direction="row" spacing={1} alignItems="flex-end">
            <TextField
              inputRef={inputRef}
              fullWidth
              size="small"
              multiline
              maxRows={3}
              placeholder="Ask GoSnel AI…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              disabled={sending}
            />
            <IconButton
              color="primary"
              onClick={() => send()}
              disabled={sending || !input.trim()}
              sx={{
                bgcolor: '#0f766e',
                color: '#fff',
                '&:hover': { bgcolor: '#0d9488' },
                '&.Mui-disabled': { bgcolor: '#cbd5e1', color: '#fff' },
              }}
            >
              {sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon fontSize="small" />}
            </IconButton>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
